import { useEffect, useState } from 'react';
import { XMLParser } from 'fast-xml-parser';
import { sampleBatchApi } from '../api/sample-batch-client';
import './SampleBatchTab.css';

interface SampleBatch {
  id: string;
  name: string;
  records: Array<{ index: number; headerData: Record<string, unknown>; fieldValues: Record<string, unknown> }>;
}

interface SampleBatchTabProps {
  jobTypeId: string;
  fields: Array<{ name: string }>;
}

export function SampleBatchTab({ jobTypeId, fields }: SampleBatchTabProps) {
  const [batches, setBatches] = useState<SampleBatch[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newHeader, setNewHeader] = useState<Record<string, unknown>>({});
  const [newValues, setNewValues] = useState<Record<string, string>>({});

  useEffect(() => {
    void sampleBatchApi.list(jobTypeId).then((rs) => setBatches(rs as SampleBatch[]));
  }, [jobTypeId]);

  async function handleFile(file: File) {
    const text = await file.text();
    const parser = new XMLParser({ ignoreAttributes: false });
    setNewHeader(parser.parse(text) as Record<string, unknown>);
  }

  async function create() {
    const created = await sampleBatchApi.create(jobTypeId, {
      name: newName,
      records: [{ headerData: newHeader, fieldValues: newValues }],
    });
    setBatches([created as SampleBatch, ...batches]);
    setCreating(false);
    setNewName('');
    setNewHeader({});
    setNewValues({});
  }

  return (
    <div className="sample-batch-tab">
      <aside className="sample-batch-tab__list">
        <button type="button" onClick={() => setCreating(true)}>New sample batch</button>
        <ul>
          {batches.map((b) => (
            <li key={b.id}>
              <button type="button" onClick={() => setSelected(b.id)}>{b.name}</button>
              <button type="button" onClick={() => sampleBatchApi.activate(b.id)}>Activate</button>
              <button
                type="button"
                onClick={async () => {
                  await sampleBatchApi.remove(b.id);
                  setBatches(batches.filter((x) => x.id !== b.id));
                }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="sample-batch-tab__detail">
        {batches.length === 0 && !creating && (
          <div className="sample-tab__empty-state">
            <h3 className="sample-tab__empty-title">No sample batches yet</h3>
            <p className="sample-tab__empty-body">
              Sample batches let you preview rules, transforms, and exports against
              representative documents before they run on real data. Documents in a
              sample batch don&apos;t move through the live queue.
            </p>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="sample-tab__empty-cta"
            >
              + New sample batch
            </button>
          </div>
        )}
        {creating && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void create();
            }}
          >
            <label>
              Name <input value={newName} onChange={(e) => setNewName(e.target.value)} />
            </label>
            <label>
              Sample header XML{' '}
              <input
                type="file"
                accept=".xml"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
            </label>
            <fieldset>
              <legend>Field values</legend>
              {fields.map((f) => (
                <label key={f.name}>
                  {f.name}
                  <input
                    value={newValues[f.name] ?? ''}
                    onChange={(e) => setNewValues({ ...newValues, [f.name]: e.target.value })}
                  />
                </label>
              ))}
            </fieldset>
            <button type="submit">Create</button>
            <button type="button" onClick={() => setCreating(false)}>Cancel</button>
          </form>
        )}
        {selected && !creating && (
          <div>Viewing {batches.find((b) => b.id === selected)?.name ?? ''}</div>
        )}
      </section>
    </div>
  );
}
